import { MsgExecuteContractCompat } from '@injectivelabs/sdk-ts'
import { msgBroadcaster } from '@shared/WalletService'
import { sharedToBalanceInWei } from '@shared/utils/formatter'
import {
  formatBaseConfig,
  formatMarketMarkingConfig,
  formatMsgPricingStrategyAmount
} from '@/app/client/utils/vaultConfig/formatter'
import { CPMMV1Vaults } from '@/app/data/vault'
import { BASIS_POINT_DECIMAL_PLACES } from '@/app/utils/constant'
import {
  UiVault,
  CPMMConfigField,
  VaultBaseConfig,
  VaultSpotConfig,
  VaultConfigForm,
  VaultConfigField,
  VaultDerivativeConfig
} from '@/types'

export const updateCPMMVaultConfig = async ({
  vault,
  formValues
}: {
  vault: UiVault
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress: vault.contractAddress,
    exec: {
      action: 'update_vault_config',
      msg: {
        ...formatBaseConfig({
          formValues,
          contractAddress: vault.contractAddress,
          config: mitoStore.vaultConfig.base as VaultBaseConfig
        }),
        pricing_strategy: formatMsgPricingStrategyAmount({
          formValues,
          contractAddress: vault.contractAddress
        }),
        max_invariant_sensitivity_bps: sharedToBalanceInWei({
          value: formValues[CPMMConfigField.MaxInvariantSensitivityBps],
          decimalPlaces: BASIS_POINT_DECIMAL_PLACES
        }).toFixed(),
        max_price_sensitivity_bps: sharedToBalanceInWei({
          value: formValues[CPMMConfigField.MaxPriceSensitivityBps],
          decimalPlaces: BASIS_POINT_DECIMAL_PLACES
        }).toFixed(),
        fee_bps: sharedToBalanceInWei({
          value: formValues[CPMMConfigField.FeeBps],
          decimalPlaces: BASIS_POINT_DECIMAL_PLACES
        }).toNumber(),
        ...(!CPMMV1Vaults.includes(vault.contractAddress)
          ? {
              order_type: formValues[CPMMConfigField.OrderType]
            }
          : {})
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await mitoStore.fetchVaultConfig({
    type: vault.vaultType,
    vaultAddress: vault.contractAddress
  })
}

export const updateSpotVaultConfig = async ({
  vault,
  formValues
}: {
  vault: UiVault
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress: vault.contractAddress,
    exec: {
      action: 'update_vault_config',
      msg: {
        ...formatBaseConfig({
          formValues,
          contractAddress: vault.contractAddress,
          config: mitoStore.vaultConfig.base as VaultBaseConfig
        }),
        ...formatMarketMarkingConfig({
          formValues,
          config: (mitoStore.vaultConfig as VaultSpotConfig).marketMaking
        }),
        oracle_type: Number(formValues[VaultConfigField.OracleType]),
        target_base_weight: formValues[VaultConfigField.TargetBaseWeight],
        allowed_redemption_types: Number(
          formValues[VaultConfigField.AllowedSpotRedemptionType]
        )
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await mitoStore.fetchVaultConfig({
    type: vault.vaultType,
    vaultAddress: vault.contractAddress
  })
}

export const updateOffChainVaultConfig = async ({
  vault,
  isSpot,
  formValues
}: {
  vault: UiVault
  isSpot: boolean
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress: vault.contractAddress,
    exec: {
      action: 'update_vault_config',
      msg: {
        oracle_stale_time: formValues[VaultConfigField.OracleStaleTime],
        notional_value_cap: formValues[VaultConfigField.NotionalValueCap],
        withdrawal_admin_fee_bps: sharedToBalanceInWei({
          value: formValues[VaultConfigField.WithdrawalAdminFeeBps],
          decimalPlaces: BASIS_POINT_DECIMAL_PLACES
        }).toNumber(),
        inj_reward_commission_bps: sharedToBalanceInWei({
          value: formValues[VaultConfigField.InjRewardCommissionBps],
          decimalPlaces: BASIS_POINT_DECIMAL_PLACES
        }).toNumber(),
        vault_type: isSpot
          ? {
              Spot: {
                oracle_type: Number(formValues[VaultConfigField.OracleType]),
                base_oracle_symbol:
                  formValues[VaultConfigField.BaseOracleSymbol],
                quote_oracle_symbol:
                  formValues[VaultConfigField.QuoteOracleSymbol],
                base_decimals: Number(
                  formValues[VaultConfigField.BaseDecimals]
                ),
                quote_decimals: Number(
                  formValues[VaultConfigField.QuoteDecimals]
                )
              }
            }
          : {
              Derivative: {
                position_pnl_penalty:
                  formValues[VaultConfigField.PositionPnLPenalty],
                allowed_derivative_redemption_types: Number(
                  formValues[VaultConfigField.AllowedDerivativeRedemptionType]
                )
              }
            }
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await mitoStore.fetchVaultConfig({
    type: vault.vaultType,
    vaultAddress: vault.contractAddress
  })
}

export const updateDerivativeVaultConfig = async ({
  vault,
  formValues
}: {
  vault: UiVault
  formValues: VaultConfigForm
}) => {
  const mitoStore = useMitoStore()
  const walletStore = useWalletStore()
  const sharedWalletStore = useSharedWalletStore()

  if (!sharedWalletStore.isUserConnected) {
    return
  }

  await walletStore.validateAndQueue()

  const message = MsgExecuteContractCompat.fromJSON({
    contractAddress: vault.contractAddress,
    exec: {
      action: 'update_vault_config',
      msg: {
        ...formatBaseConfig({
          formValues,
          contractAddress: vault.contractAddress,
          config: mitoStore.vaultConfig.base as VaultBaseConfig
        }),
        ...formatMarketMarkingConfig({
          formValues,
          config: (mitoStore.vaultConfig as VaultDerivativeConfig).marketMaking
        }),
        leverage: formValues[VaultConfigField.Leverage],
        min_proximity_to_liquidation:
          formValues[VaultConfigField.MinProximityToLiquidation],
        allowed_redemption_types: Number(
          formValues[VaultConfigField.AllowedDerivativeRedemptionType]
        )
      }
    },
    sender: sharedWalletStore.injectiveAddress
  })

  await msgBroadcaster.broadcastV2({
    injectiveAddress: sharedWalletStore.injectiveAddress,
    msgs: message
  })

  await mitoStore.fetchVaultConfig({
    type: vault.vaultType,
    vaultAddress: vault.contractAddress
  })
}
